import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { designTokens } from "../../../components/ui/foundation/DesignTokens";

// ============================================
// CONTENT WRAPPER FOR DRAWER LAYOUT
// ============================================
export const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding: 40px;
  overflow-y: auto;
  background: ${designTokens.colors.gray[50]};

  @media (max-width: 900px) {
    padding: 24px 16px;
  }
`;

// ============================================
// KPI GRID & CARDS
// ============================================
export const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const KPICard = styled.article`
  background: white;
  border-radius: ${designTokens.radius.lg};
  padding: 24px;
  box-shadow: ${designTokens.shadows.sm};
  border: 1px solid ${designTokens.colors.gray[200]};
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

export const KPICardTitle = styled.h3`
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${designTokens.colors.gray[600]};
  margin: 0;
  line-height: 1.4;
`;

export const KPICardValue = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.color || designTokens.colors.gray[900]};
  line-height: 1.2;
`;

export const KPICardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${designTokens.radius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bgColor || 'rgba(79, 70, 229, 0.1)'};

  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.iconColor || '#4F46E5'};
  }
`;

// ============================================
// FILTER CONTROLS (Date Pickers + Buttons)
// ============================================
export const FilterToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  background: white;
  padding: 12px 20px;
  border-radius: ${designTokens.radius.full};
  border: 1px solid ${designTokens.colors.gray[200]};
  box-shadow: ${designTokens.shadows.sm};
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    border-radius: ${designTokens.radius.lg};
    padding: 16px;
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const FilterLabel = styled.label`
  font-family: ${designTokens.fonts.body};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${designTokens.colors.gray[500]};
  white-space: nowrap;
`;

export const DatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .MuiFormControl-root {
    background: transparent;
    min-width: 140px;
    margin: 0;
  }

  .MuiInputBase-root {
    font-family: ${designTokens.fonts.body};
    font-size: 0.85rem;
    color: ${designTokens.colors.gray[600]};
    border: 1px solid ${designTokens.colors.gray[200]};
    border-radius: ${designTokens.radius.full};
    padding: 4px 12px;
    background: white;

    &:hover {
      border-color: ${designTokens.colors.gray[300]};
    }

    &.Mui-focused {
      border-color: ${designTokens.colors.primary[500]};
    }
  }

  .MuiInputBase-input {
    padding: 6px 0;
    cursor: pointer;
  }

  .MuiInputLabel-root {
    display: none;
  }

  .MuiInput-underline:before,
  .MuiInput-underline:after {
    display: none;
  }

  .MuiIconButton-root {
    padding: 4px;
    color: ${designTokens.colors.gray[400]};
  }
`;

export const DateArrow = styled.span`
  color: ${designTokens.colors.gray[400]};
  font-size: 0.9rem;
`;

export const DateRangeHelperText = styled.span`
  display: block;
  font-family: ${designTokens.fonts.body};
  font-size: 0.72rem;
  color: ${designTokens.colors.gray[400]};
  margin-top: 3px;
  white-space: nowrap;
`;

export const DatePickerColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const ChannelMuiSelectWrapper = styled.div`
  display: flex;
  align-items: center;

  /* Wrap the MUI Select in a pill-shaped container matching the toolbar style */
  .MuiSelect-root {
    font-family: ${designTokens.fonts.body};
    font-size: 0.85rem;
    color: ${designTokens.colors.gray[700]};
    background: white;
    border: 1px solid ${designTokens.colors.gray[200]};
    border-radius: ${designTokens.radius.full};
    padding: 6px 32px 6px 12px;
    min-width: 180px;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    align-items: center;

    &:hover {
      border-color: ${designTokens.colors.gray[300]};
    }

    &:focus {
      border-color: ${designTokens.colors.primary[400]};
      box-shadow: 0 0 0 3px ${designTokens.colors.primary[100]};
      background: white;
      border-radius: ${designTokens.radius.full};
    }
  }

  /* Position the dropdown arrow inside the pill */
  .MuiSelect-icon {
    right: 10px;
    color: ${designTokens.colors.gray[400]};
    font-size: 1.1rem;
  }

  /* Remove the default MUI input outline/underline */
  .MuiInput-root,
  .MuiInputBase-root {
    border: none;

    &::before,
    &::after {
      display: none;
    }
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
  }
`;

// ============================================
// LOADER & ERROR STATES
// ============================================
export const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

export const ErrorBox = styled.div`
  background: #FEE2E2;
  border: 1px solid #FECACA;
  border-radius: ${designTokens.radius.md};
  padding: 16px 20px;
  color: #B91C1C;
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
`;

export const WarningBanner = styled.div`
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border: 1px solid #F59E0B;
  border-radius: ${designTokens.radius.lg};
  padding: 16px 24px;
  color: #92400E;
  font-family: ${designTokens.fonts.body};
  font-size: 0.9rem;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    font-size: 1.25rem;
  }

  a {
    color: #B45309;
    font-weight: 600;
    text-decoration: underline;

    &:hover {
      color: #92400E;
    }
  }

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
    padding: 14px 16px;
    font-size: 0.85rem;
  }
`;

// ============================================
// TABLE WRAPPER
// ============================================
export const TableWrapper = styled.div`
  background: white;
  border-radius: ${designTokens.radius.lg};
  box-shadow: ${designTokens.shadows.sm};
  border: 1px solid ${designTokens.colors.gray[200]};
  overflow: hidden;
`;

export const TableTitle = styled.h2`
  font-family: ${designTokens.fonts.heading};
  font-size: 1.125rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[900]};
  margin: 0;
  padding: 20px 24px;
  border-bottom: 1px solid ${designTokens.colors.gray[200]};
`;

// ============================================
// AGENT CELL STYLES
// ============================================
export const AgentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const AgentAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${designTokens.radius.full};
  background: ${props => props.bgColor || designTokens.colors.primary[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${designTokens.fonts.heading};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.textColor || designTokens.colors.primary[700]};
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const AgentName = styled.span`
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${designTokens.colors.gray[900]};
`;

// ============================================
// RATING DISPLAY
// ============================================
export const RatingDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
    color: #FBBF24;
  }
`;

export const RatingValue = styled.span`
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${designTokens.colors.gray[900]};
  margin-left: 4px;
`;

// ============================================
// EMPTY STATE
// ============================================
export const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: ${designTokens.colors.gray[500]};
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
`;

// ============================================
// LEGACY COMPONENTS (for AnaliticsWithAgents.jsx)
// ============================================
export const Title = styled.h1`
  font-family: ${designTokens.fonts.heading};
  font-size: 1.5rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[900]};
  margin: 0;
`;

export const CardsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
`;

export const PickerSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
`;

export const ExportButtonIcon = styled(FontAwesomeIcon)`
  margin-right: 8px;
`;

export const DatePicker = styled.div`
  display: flex;
  gap: 16px;

  .MuiFormControl-root {
    min-width: 150px;
  }
`;

export const Table = styled.div`
  background: white;
  border-radius: ${designTokens.radius.lg};
  box-shadow: ${designTokens.shadows.sm};
  border: 1px solid ${designTokens.colors.gray[200]};
  overflow: hidden;
`;

export const CardAgentInfo = styled.div`
  background: white;
  border-radius: ${designTokens.radius.lg};
  box-shadow: ${designTokens.shadows.sm};
  border: 1px solid ${designTokens.colors.gray[200]};
  padding: 24px;
  margin-bottom: 24px;
`;

export const CardAgentInfoTitle = styled.h3`
  font-family: ${designTokens.fonts.heading};
  font-size: 1.125rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[900]};
  margin: 0 0 16px 0;
`;

export const CardAgentInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const CardAgentInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const CardAgentInfoItemTitle = styled.span`
  font-family: ${designTokens.fonts.body};
  font-size: 0.75rem;
  font-weight: 500;
  color: ${designTokens.colors.gray[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const CardAgentInfoItemValue = styled.span`
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${designTokens.colors.gray[900]};
`;

export const CardChatInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
